import React, { useEffect, useState } from 'react'
import { Alert, Image, TouchableOpacity, View } from 'react-native'
import * as ImagePicker from 'expo-image-picker'
import { ArrowLeft, Store, ChevronRight, Camera, QrCode } from 'lucide-react-native'
import { CustomerQRDisplay } from '../components/CustomerQR'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '@fliisi/i18n'
import { supabase } from '../lib/supabase'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, IconButton, Input, LanguageSwitcher, ListItem,
  Spinner, Text, TopBar, XStack, YStack, Sheet,
} from '@fliisi/pollen'
import { ScreenLayout } from '../components/ScreenLayout'

interface UserProfile {
  display_name: string | null; first_name: string | null; last_name: string | null
  email: string; phone: string | null; avatar_url: string | null; preferred_language: string
}

export default function ProfileSettingsScreen() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [showQR, setShowQR] = useState(false)

  const fetchProfile = async () => {
    if (!user?.id) return
    try {
      const { data, error } = await supabase
        .from('users')
        .select('display_name, first_name, last_name, email, phone, avatar_url, preferred_language')
        .eq('id', user.id).single()
      if (error) throw error
      if (data) {
        setProfile(data)
        setDisplayName(data.display_name || '')
        setFirstName(data.first_name || '')
        setLastName(data.last_name || '')
        setPhone(data.phone || '')
      }
    } catch (e) { console.error('Error fetching profile:', e) }
    finally { setLoading(false) }
  }

  const pickAvatar = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images' as any],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      })
      if (!result.canceled && result.assets[0]) {
        await uploadAvatar(result.assets[0].uri)
      }
    } catch (e) { console.error('pickAvatar error:', e) }
  }

  const uploadAvatar = async (uri: string) => {
    if (!user?.id) return
    setUploadingAvatar(true)
    try {
      const response = await fetch(uri)
      const blob = await response.blob()
      const fileName = `${user.id}/avatar-${Date.now()}.jpg`
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, blob, { contentType: 'image/jpeg', upsert: true })
      if (uploadError) throw uploadError
      const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(fileName)
      await supabase.from('users').update({ avatar_url: urlData.publicUrl }).eq('id', user.id)
      setProfile(prev => prev ? { ...prev, avatar_url: urlData.publicUrl } : prev)
    } catch (e) {
      console.error('uploadAvatar error:', e)
      Alert.alert(t('error'), t('save_failed'))
    } finally { setUploadingAvatar(false) }
  }

  useEffect(() => {
    if (!user?.id) { navigation.replace('Login'); return }
    fetchProfile()
  }, [user?.id])

  const handleSave = async () => {
    if (!user?.id) return
    setSaving(true)
    try {
      const { error } = await supabase.from('users').update({
        display_name: displayName.trim() || null,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        phone: phone.trim() || null,
        preferred_language: language,
      }).eq('id', user.id)
      if (error) throw error
      Alert.alert(t('done'), t('profile_updated'))
      navigation.goBack()
    } catch (e) {
      console.error('Error saving profile:', e)
      Alert.alert(t('error'), t('save_failed'))
    } finally { setSaving(false) }
  }

  const hasChanges = () => {
    if (!profile) return false
    return displayName !== (profile.display_name || '') || firstName !== (profile.first_name || '') ||
      lastName !== (profile.last_name || '') || phone !== (profile.phone || '')
  }

  const getInitials = () => {
    const name = displayName || `${firstName} ${lastName}`.trim() || profile?.email?.split('@')[0] || t('user')
    const parts = name.split(' ')
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
  }

  const topBar = (
    <TopBar
      title={t('settings')}
      left={
        <IconButton variant="ghost" size="lg" onPress={() => navigation.goBack()}>
          <ArrowLeft size={20} color="#748C70" />
        </IconButton>
      }
      right={
        saving ? <Spinner size="small" /> : (
          <Button variant={hasChanges() ? 'primary' : 'secondary'} size="sm" onPress={handleSave} disabled={!hasChanges() || saving}>
            <ButtonText variant={hasChanges() ? 'primary' : 'secondary'}>{t('save')}</ButtonText>
          </Button>
        )
      }
    />
  )

  if (loading) {
    return (
      <YStack f={1} bg="$background">
        {topBar}
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="$background">
      {topBar}
      <ScreenLayout>
        {/* Avatar */}
        <YStack ai="center" py="$lg">
          <TouchableOpacity onPress={pickAvatar} disabled={uploadingAvatar} style={{ marginBottom: 10 }}>
            <View style={{ position: 'relative' }}>
              {profile?.avatar_url ? (
                <Image source={{ uri: profile.avatar_url }} style={{ width: 88, height: 88, borderRadius: 44 }} />
              ) : (
                <View style={{ width: 88, height: 88, borderRadius: 44, backgroundColor: '#748C70', alignItems: 'center', justifyContent: 'center' }}>
                  {uploadingAvatar
                    ? <Spinner size="small" color="#fff" />
                    : <Text fontSize={32} fontWeight="700" color="#fff">{getInitials()}</Text>
                  }
                </View>
              )}
              {/* Camera badge */}
              <View style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: 14, backgroundColor: '#fff', borderWidth: 2, borderColor: '#F5F3EF', alignItems: 'center', justifyContent: 'center' }}>
                {uploadingAvatar
                  ? <Spinner size="small" />
                  : <Camera size={14} color="#748C70" />
                }
              </View>
            </View>
          </TouchableOpacity>
          <XStack ai="center" gap="$sm" mt={2}>
            <Text preset="bodySmall" tone="secondary">{profile?.email}</Text>
            <XStack
              ai="center" gap={4} px={8} py={4} br="$full"
              bg="rgba(116,140,112,0.1)"
              onPress={() => setShowQR(true)}
              pressStyle={{ opacity: 0.7 }}
              cursor="pointer"
            >
              <QrCode size={13} color="#748C70" />
              <Text preset="caption" color="$colorPrimary">QR</Text>
            </XStack>
          </XStack>
        </YStack>

        <Sheet open={showQR} onOpenChange={setShowQR} snapPoints={[55]} noHandle={false}>
          <YStack px="$md" pt="$sm" pb="$xl" ai="center">
            <CustomerQRDisplay size={220} />
          </YStack>
        </Sheet>

        {/* Public Profile */}
        <YStack mb="$lg">
          <Text preset="caption" tone="muted" mb="$sm" letterSpacing={0.8}>{t('public_profile').toUpperCase()}</Text>
          <Input value={displayName} onChangeText={setDisplayName} placeholder={t('display_name_placeholder')} />
        </YStack>

        {/* Personal Info */}
        <YStack mb="$lg">
          <Text preset="caption" tone="muted" mb="$sm" letterSpacing={0.8}>{t('personal_info').toUpperCase()}</Text>
          {[
            { label: t('first_name'), value: firstName, setter: setFirstName, placeholder: t('first_name') },
            { label: t('last_name'), value: lastName, setter: setLastName, placeholder: t('last_name') },
            { label: t('phone'), value: phone, setter: setPhone, placeholder: t('phone_placeholder'), kb: 'phone-pad' as any },
          ].map(field => (
            <Input key={field.label} value={field.value} onChangeText={field.setter}
              placeholder={field.placeholder} keyboardType={field.kb} mb="$xs" />
          ))}
        </YStack>

        {/* Language */}
        <YStack mb="$lg">
          <Text preset="caption" tone="muted" mb="$sm" letterSpacing={0.8}>{t('language').toUpperCase()}</Text>
          <LanguageSwitcher />
        </YStack>

        {/* Merchant Portal */}
        <YStack mb="$lg">
          <Text preset="caption" tone="muted" mb="$sm" letterSpacing={0.8}>{t('merchant').toUpperCase()}</Text>
          <ListItem
            icon={
              <YStack w={44} h={44} br={12} bg="rgba(184,115,51,0.1)" jc="center" ai="center">
                <Store size={22} color="#B87333" />
              </YStack>
            }
            title={t('merchant_portal')}
            subtitle={t('manage_stores')}
            right={<ChevronRight size={18} color="#858B80" />}
            onPress={() => navigation.navigate('MerchantStoreSelect')}
            bg="$backgroundCard" br="$xl" p="$md"
            borderWidth={1} borderColor="rgba(184,115,51,0.2)"
          />
        </YStack>

        <YStack h={40} />
      </ScreenLayout>
    </YStack>
  )
}
